import { useMemo } from 'react';
import { CONTENT_TAG_CATEGORIES_MAP } from 'utils/constants';
import {
  makeTreeFromTags,
  makeTreeToRootFromTags,
  useContentTags,
  DEFAULT_TREE_ROOT_CATEGORIES,
} from 'lib/content-tags';
import { useAliasedContentTags } from 'lib/content-tags/use-aliased-content-tags';
import { uniqBy, keyBy } from 'lodash';

const MEEOPP_TOPIC_DUMMY_VALUE = 'MEEOPP_TOPIC_DUMMY_VALUE';

export const useThemeOptionsByCurriculum = (curriculumIds) => {
  const { findThemeTagsByCurriculum, contentTagsMapById, contentTagsTree } = useContentTags([
    ...DEFAULT_TREE_ROOT_CATEGORIES,
    CONTENT_TAG_CATEGORIES_MAP.meeoppTopic,
    CONTENT_TAG_CATEGORIES_MAP.meeoppSubtopic,
  ]);

  const themesAndSubthemes = useMemo(() => {
    return uniqBy(
      curriculumIds.map((curriculumId) => findThemeTagsByCurriculum([curriculumId])).flat(),
      '_id'
    );
  }, [findThemeTagsByCurriculum, curriculumIds]);

  const { aliasedContentTags: aliasedThemesAndSubthemes } =
    useAliasedContentTags(themesAndSubthemes);

  const getThemeOptions = () => {
    const meeoppTopics = contentTagsTree.filter(
      (contentTag) => contentTag.category === CONTENT_TAG_CATEGORIES_MAP.meeoppTopic
    );

    const showMeeOppTopic =
      !curriculumIds?.length ||
      curriculumIds.some((curriculumId) => {
        return !findThemeTagsByCurriculum([curriculumId]).length;
      });

    const treeUpToRoot = makeTreeToRootFromTags(aliasedThemesAndSubthemes, contentTagsMapById).map(
      (tag) => {
        return {
          ...tag,
          key: tag._id,
        };
      }
    );

    if (!showMeeOppTopic) {
      // If there is only 1 curriculum, we don't need to show the root tag
      if (treeUpToRoot.length === 1) {
        return makeTreeFromTags(aliasedThemesAndSubthemes);
      }

      return treeUpToRoot;
    }

    // If there is a curriculum but there are no themes/subthemes, we show MeeOpp Topics (without the root tag)
    if (curriculumIds.length <= 1) {
      return meeoppTopics;
    }

    return [
      ...treeUpToRoot,
      {
        _id: MEEOPP_TOPIC_DUMMY_VALUE,
        key: MEEOPP_TOPIC_DUMMY_VALUE,
        displayTitle: 'MeeOpp Topic',
        title: 'MeeOpp Topic',
        enUSTitle: 'MeeOpp Topic',
        checkable: false,
        value: MEEOPP_TOPIC_DUMMY_VALUE,
        children: meeoppTopics.filter((contentTag) => {
          return contentTag.category === CONTENT_TAG_CATEGORIES_MAP.meeoppTopic;
        }),
      },
    ];
  };

  const getThemeOptionsById = (themeOptions) => {
    return themeOptions.reduce(
      (acc, themeOption) => ({
        ...acc,
        ...getThemeOptionsById(themeOption.children ?? []),
      }),
      keyBy(themeOptions, '_id')
    );
  };

  return useMemo(() => {
    const themeOptions = getThemeOptions();
    const themeOptionsById = getThemeOptionsById(themeOptions);
    return { themeOptions, themeOptionsById };
  }, [
    aliasedThemesAndSubthemes,
    contentTagsMapById,
    contentTagsTree,
    findThemeTagsByCurriculum,
    curriculumIds,
  ]);
};
